import { useEffect, useRef, useState } from 'react';
import { Loader2, RefreshCw, Ruler } from 'lucide-react';
import {
  addWrittenLines, compareGroups, dimensionLabel, dimensionLinesOf, dimensionsOf, rebuildWritten, shownGroup,
  type WrittenDimension,
} from './openaiBlenderApi';
import { setDimensionFocus } from './dimensionFocus';
import { falError, type Job3D } from './threeDApi';
import { clock, useWaited } from './waitClock';
import { IS_LOCKED } from '../../apiBase';
import { useLanguage } from '../../contexts/LanguageContext';

// THE NUMBERS A WRITTEN MODEL WAS BUILT FROM, and a way to change them without asking anybody.
//
// A written script opens with its dimensions — seat height, leg thickness, splay — and builds
// everything from them (see SYSTEM in openai_blender). So "a little taller" is not another
// six-minute call: it is one number changed, the same script run again in Blender, seconds and
// no tokens. The result is saved as the next pass of the run, so the pass toggle above the
// viewer compares it with the model it changed.
//
// Nothing for a run whose script has no block: every run made before the rule, and a model
// that ignored it.
export function WrittenDimensionsPanel({ job, disabled, onRebuilt }: {
  job: Job3D;
  disabled?: boolean;
  // The rebuilt run, saved. The caller shows it and refreshes its list.
  onRebuilt: (job: Job3D) => void;
}) {
  const { t } = useLanguage();
  const dims = dimensionsOf(job);
  // DEAD WHERE NOTHING MAY BE SPENT OR WRITTEN — the demo and the sandbox. A rebuild is a Blender
  // run saved as a new pass, and this panel is outside PhaseControls, whose lock does not reach it.
  const off = disabled || IS_LOCKED;
  // What has been typed, by name, as typed. Absent is the script's own number.
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const waited = useWaited(busy);
  // THE CALL THAT SAYS WHERE AN OLD MODEL'S DIMENSIONS ARE MEASURED — see addWrittenLines. Its own
  // flag and clock: it is a model call of a minute, where a rebuild is a Blender of seconds.
  const [drawing, setDrawing] = useState(false);
  const drawn = useWaited(drawing);
  const hasLines = dimensionLinesOf(job).length > 0;
  const [error, setError] = useState('');
  // Another run on screen is another script: nothing typed against the last one carries over.
  useEffect(() => {
    setTyped({});
    setError('');
  }, [job.id]);
  // A caret that goes with the panel takes its line with it.
  useEffect(() => () => setDimensionFocus(null), []);
  // HOW MANY FIELDS FIT ACROSS, measured rather than left to the grid: a grid fills its rows
  // from the left and leaves the last one ragged, and the rows here are balanced and centred
  // (see rowsOf), which needs the count before they are drawn. Measured again on a resize.
  const box = useRef<HTMLDivElement>(null);
  const [cols, setCols] = useState(5);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setCols(Math.max(1, Math.floor(
      (el.clientWidth - CARD_INSET_PX + GAP_PX) / (FIELD_PX + GAP_PX))));
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(el);
    return () => watch.disconnect();
  }, [job.id]);
  if (!dims.length) return null;

  const changed: Record<string, number> = {};
  let invalid = false;
  for (const d of dims) {
    const said = typed[d.name];
    if (said === undefined) continue;
    const value = Number(said.replace(',', '.'));
    if (said.trim() === '' || !Number.isFinite(value)) invalid = true;
    else if (value !== d.value) changed[d.name] = value;
  }
  const count = Object.keys(changed).length;
  // One width for every field in the panel, whatever row it is in: the width a full row gives.
  const fieldWidth = `calc((100% - ${(cols - 1) * GAP_PX}px) / ${cols})`;

  const addLines = async () => {
    setDrawing(true);
    setError('');
    try {
      onRebuilt(await addWrittenLines(job.id));
    } catch (e) {
      setError(falError(e, t('notice.3d.linesFailed')));
    } finally {
      setDrawing(false);
    }
  };

  const rebuild = async () => {
    setBusy(true);
    setError('');
    try {
      onRebuilt(await rebuildWritten(job.id, changed));
    } catch (e) {
      setError(falError(e, t('notice.3d.dimsFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="w-full">
      <h5 className="mb-2 text-center text-[11px] font-bold uppercase tracking-wider text-slate-500">
        {t('notice.3d.dimsTitle')}
      </h5>
      {/* A MESH EACH, under its name — the groups the script gave its dimensions, in its own
          order, each in a box of its own so where one mesh's numbers stop is drawn rather
          than guessed from a heading. A script written before it was asked to group them has
          one untitled group, which is the list as it always was. */}
      <div ref={box} className="flex w-full flex-col gap-3">
      {groupsOf(dims).map((group, at) => (
        <div key={`${at}-${group.title}`} className="rounded-xl border border-slate-200 p-3">
          {group.title && (
            // A hairline under the mesh's name, the box's full width, between it and its fields.
            <h6 className="-mx-3 mb-2.5 border-b border-slate-200 px-3 pb-2 text-center text-sm font-semibold text-blue-700">
              {group.title}
            </h6>
          )}
          {/* BALANCED ROWS, EACH CENTRED: as few rows as fit, as even as they can be, the
              longer ones first — and every field the same width, so a short row sits in the
              middle rather than hanging off the left edge. */}
          <div className="flex flex-col gap-2">
            {rowsOf(group.dims, cols).map((row, r) => (
              <div key={r} className="flex justify-center gap-2">
                {row.map((d) => (
                  // The model's own note in the tooltip — "top of the seat, from the floor" — which is
                  // what says whether a number is millimetres, degrees or a count.
                  <label key={d.name} title={d.note ? `${d.name} — ${d.note}` : d.name} style={{ width: fieldWidth }} className="flex shrink-0 flex-col gap-0.5">
                    <span className="truncate text-center text-[11px] font-bold text-slate-500">{dimensionLabel(d)}</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="any"
                      value={typed[d.name] ?? String(d.value)}
                      disabled={busy || drawing || off}
                      onChange={(e) => setTyped((all) => ({ ...all, [d.name]: e.target.value }))}
                      // WHICH LINE THIS NUMBER IS, drawn on the model above while the caret is here.
                      onFocus={() => setDimensionFocus(d.name)}
                      onBlur={() => setDimensionFocus(null)}
                      className={`w-full rounded-lg border bg-white px-2 py-1 text-sm tabular-nums text-slate-800 ${
                        d.name in changed
                          ? 'border-blue-500'
                          : 'border-slate-300'
                      }`}
                    />
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => void rebuild()}
          disabled={busy || drawing || off || invalid || count === 0}
          className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {t(busy ? 'notice.3d.dimsRebuilding' : 'notice.3d.dimsRebuild')}
          {/* HOW LONG IT HAS BEEN BUILDING, in the button that started it — a Blender run is
              seconds to a few minutes, and nothing else on the page moves while it goes. */}
          {busy && <span className="tabular-nums opacity-80">({clock(waited)})</span>}
        </button>
        {/* A MODEL BUILT BEFORE ITS SCRIPT SAID WHERE ITS DIMENSIONS ARE MEASURED gets them from a
            small call, as a new version — refused if a mesh moved. Dead where nothing may be
            spent, like the rebuild beside it. */}
        {!hasLines && (
          <button
            type="button"
            onClick={() => void addLines()}
            disabled={busy || drawing || off}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[12.5px] font-semibold text-slate-700 transition-colors hover:border-blue-500 hover:text-blue-600 disabled:opacity-40"
          >
            {drawing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ruler className="h-3.5 w-3.5" />}
            {t(drawing ? 'notice.3d.linesAdding' : 'notice.3d.linesAdd')}
            {drawing && <span className="tabular-nums opacity-80">({clock(drawn)})</span>}
          </button>
        )}
        {error && <span className="text-[11px] text-rose-500">{error}</span>}
      </div>
    </section>
  );
}

// The narrowest a field may be, and the gap between two — the grid's old `minmax(10rem, …)` and
// `gap-2`, in the pixels the measurement is taken in.
const FIELD_PX = 160;
const GAP_PX = 8;
// Each group's box: its padding (p-3) and hairline border on either side, which the fields do
// not get to use.
const CARD_INSET_PX = 2 * (12 + 1);

// A group's fields in rows of at most `cols`: as few rows as that allows, as even as they can
// be, and where they cannot be even the earlier rows take the extra — 7 across 5 is 4 then 3.
function rowsOf<T>(items: T[], cols: number): T[][] {
  if (!items.length) return [];
  const rows = Math.ceil(items.length / Math.max(1, cols));
  const base = Math.floor(items.length / rows);
  const extra = items.length % rows;
  const out: T[][] = [];
  for (let r = 0, at = 0; r < rows; r += 1) {
    const n = base + (r < extra ? 1 : 0);
    out.push(items.slice(at, at + n));
    at += n;
  }
  return out;
}

// THE GROUPS, IN THE ORDER THEY ARE READ — see compareGroups. The order is this panel's rather
// than the script's, so a dimension written further down under a group already begun joins it.
const groupsOf = (dims: WrittenDimension[]) => {
  const by = new Map<string, WrittenDimension[]>();
  for (const d of dims) {
    const title = shownGroup(d.group || '');
    by.set(title, [...(by.get(title) ?? []), d]);
  }
  return [...by.entries()]
    .map(([title, grouped]) => ({ title, dims: grouped }))
    .sort((x, y) => compareGroups(x.title, y.title));
};

